import * as React from "react";

import { generateValidClassNameHandler } from "@/lib/utils";

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({
    className,
    ...props
}, ref) => (
    <div className="relative w-full overflow-auto">
        <table
            ref={ref}
            className={generateValidClassNameHandler(
                "w-full caption-bottom text-xs md:text-sm",
                className,
            )}
            {...props}
        />
    </div>
));
Table.displayName = "Table";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({
    className,
    ...props
}, ref) => (
    <thead
        ref={ref}
        className={generateValidClassNameHandler(
            "[&_tr]:border-b",
            className,
        )}
        {...props}
    />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({
    className,
    ...props
}, ref) => (
    <tbody
        ref={ref}
        className={generateValidClassNameHandler(
            "[&_tr:last-child]:border-0",
            className,
        )}
        {...props}
    />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<
    HTMLTableSectionElement,
    React.HTMLAttributes<HTMLTableSectionElement>
>(({
    className,
    ...props
}, ref) => (
    <tfoot
        ref={ref}
        className={generateValidClassNameHandler(
            "border-t bg-gray-200/50 font-medium [&>tr]:last:border-b-0",
            className,
        )}
        {...props}
    />
));
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({
    className,
    ...props
}, ref) => (
    <tr
        ref={ref}
        className={generateValidClassNameHandler(
            "border-b transition-colors hover:bg-gray-200/50 data-[state=selected]:bg-gray-200",
            className,
        )}
        {...props}
    />
));
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({
    className,
    ...props
}, ref) => (
    <th
        ref={ref}
        className={generateValidClassNameHandler(
            "h-12 px-2 md:px-4 text-left align-middle font-medium text-gray-500 [&:has([role=checkbox])]:pr-0",
            className,
        )}
        {...props}
    />
));
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({
    className,
    ...props
}, ref) => (
    <td
        ref={ref}
        className={generateValidClassNameHandler(
            "px-2 md:px-4 align-middle [&:has([role=checkbox])]:pr-0",
            className,
        )}
        {...props}
    />
));
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({
    className,
    ...props
}, ref) => (
    <caption
        ref={ref}
        className={generateValidClassNameHandler(
            "mt-2 md:mt-4 text-sm text-gray-500",
            className,
        )}
        {...props}
    />
));
TableCaption.displayName = "TableCaption";

export {
    Table,
    TableBody,
    TableCaption,
    TableCell,
    TableFooter,
    TableHead,
    TableHeader,
    TableRow,
};
